import type { SearchResponse } from 'instantsearch.js';
import { useCallback, useEffect, useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';

import type { RefineListProps } from '@sorare/core/src/components/search/RefineList';
import { AtomList } from '@sorare/core/src/components/search/RefineList';
import { useFacet, toggle } from '@sorare/core/src/contexts/graphql-search';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import type { AvailableLanguage } from '@sorare/core/src/lib/i18n.ts';

import { RefineListTitle } from '#src/search/RefineListTitle/index.tsx';
import {
  findSoleTranslation,
  findTranslationAmongItems,
} from '#src/searchCards/RefineI18nList/translation.ts';

type Props = {
  visible: boolean;
  filter: Filter & { i18nAttribute: (language: AvailableLanguage) => string };
  filterBy?: (item: any) => boolean;
  sortBy?: RefineListProps['sortBy'];
  limit?: number;
  showMore?: boolean;
  searchable?: boolean;
  renderBefore?: RefineListProps['renderBefore'];
};

const formatOption = (label: string) => label.split('|')[0];
const MAX_SHOW_MORE_LIMIT = 40;

const RefineI18nList = ({
  visible,
  filter,
  filterBy,
  sortBy = ['isRefined:desc', 'name:asc'],
  renderBefore,
  limit: limitProps,
  showMore,
  searchable,
  ...rest
}: Props) => {
  const track = useEvents();
  const { language } = useIntlContext();
  const i18nAttribute = filter.i18nAttribute(language);

  const { up: isTabletOrDesktop } = useScreenSize('tablet');

  const limit = (() => {
    if (limitProps) return limitProps;
    if (isTabletOrDesktop) return 10;
    return MAX_SHOW_MORE_LIMIT / 2;
  })();

  const showMoreLimit = MAX_SHOW_MORE_LIMIT;

  const {
    items,
    isFromSearch,
    searchForItems,
    refine: doRefine,
    hasExhaustiveItems,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute: i18nAttribute,
    sortBy,
    limit: MAX_SHOW_MORE_LIMIT - 1,
    showMoreLimit,
    ...rest,
    showMore,
  });

  const searchClient = useSearchClient();
  const { results } = useInstantSearch();

  const actualItems = useMemo(
    () =>
      items.filter(item => (filterBy ? filterBy(item) : true)).slice(0, limit),
    [filterBy, items, limit]
  );

  // A refined value with no result is either written in another language and needs translating to
  // its current-language equivalent, or part of a filter combination that simply matches nothing.
  // TODO(su): remove once most URLs in the wild are using the new format
  const untranslatedItem = items.find(
    item => item.isRefined && item.count === 0
  );

  const translateRefinement = useCallback(
    (from: string, to: string) => {
      doRefine(from);
      doRefine(to);
    },
    [doRefine]
  );

  useEffect(() => {
    if (!untranslatedItem) {
      return undefined;
    }

    const translationAmongItems = findTranslationAmongItems(
      items,
      untranslatedItem
    );
    if (translationAmongItems) {
      translateRefinement(untranslatedItem.value, translationAmongItems);
      return undefined;
    }

    const [name, code] = untranslatedItem.label.split('|');
    let outdated = false;
    searchClient
      .search([
        {
          indexName: results.index,
          type: 'default',
          params: {
            analytics: false,
            distinct: false,
            hitsPerPage: 0,
            facets: [i18nAttribute],
            filters: `(${filter.attribute}:"${name}" OR ${filter.attribute}:"${code}")`,
          },
        },
      ])
      .then(({ results: res }) => {
        const facetValues = (res[0] as SearchResponse<any>).facets?.[
          i18nAttribute
        ];
        const translation = findSoleTranslation(
          Object.keys(facetValues ?? {}),
          untranslatedItem
        );
        if (!outdated && translation) {
          translateRefinement(untranslatedItem.value, translation);
        }
      });

    return () => {
      outdated = true;
    };
  }, [
    filter.attribute,
    i18nAttribute,
    items,
    results.index,
    searchClient,
    translateRefinement,
    untranslatedItem,
  ]);

  const refine = useCallback(
    (value: string) => {
      track('Use Market Filter', {
        filterName: filter.attribute,
        filterValue: value.split('|')[1],
      });

      doRefine(value);
    },
    [doRefine, filter.attribute, track]
  );

  return (
    <AtomList
      {...rest}
      attribute={i18nAttribute}
      items={actualItems}
      formatOption={formatOption}
      refine={refine}
      renderBefore={renderBefore}
      searchForItems={searchForItems}
      isFromSearch={isFromSearch}
      searchable={typeof searchable === 'boolean' ? searchable : true}
      visible={visible}
      hasExhaustiveItems={showMore === false ? true : hasExhaustiveItems}
      isShowingMore={isShowingMore}
      toggleShowMore={toggleShowMore}
    />
  );
};

export const RefineBackendI18nList = ({
  visible,
  attribute,
  filterBy,
  renderBefore,
  limit: limitProps,
  showMore,
  searchable,
  ...rest
}: Omit<Props, 'filter'> & { attribute: string }) => {
  const { items, refine, search, isFromSearch } = useFacet(attribute);

  const filteredItems = filterBy ? items.filter(filterBy) : items;
  const { up: isTabletOrDesktop } = useScreenSize('tablet');

  const limit = (() => {
    if (limitProps) return limitProps;
    if (isTabletOrDesktop) return 10;
    return MAX_SHOW_MORE_LIMIT / 2;
  })();

  return (
    <AtomList
      {...rest}
      attribute={attribute}
      items={filteredItems.slice(0, limit)}
      formatOption={formatOption}
      refine={value => refine(toggle([value]))}
      renderBefore={renderBefore}
      searchForItems={search}
      isFromSearch={isFromSearch}
      searchable={typeof searchable === 'boolean' ? searchable : true}
      visible={visible}
    />
  );
};

const RefineI18nListTitle = ({ filter }: Pick<Props, 'filter'>) => {
  const { language } = useIntlContext();

  return (
    <RefineListTitle
      attribute={filter.i18nAttribute(language)}
      name={filter.title}
    />
  );
};

export const buildRefineI18nList = ({
  filter,
  hideTitle,
  ...rest
}: Omit<Props, 'visible'> & {
  hideTitle?: boolean;
}): FilterWidget => ({
  key: filter.attribute,
  type: 'list',
  attribute: filter.attribute,
  Component: ({ visible }) => (
    <RefineI18nList visible={visible} filter={filter} {...rest} />
  ),
  title: hideTitle ? null : <RefineI18nListTitle filter={filter} />,
  accordionOptions: { startsOpen: false },
});
